import { useState, useCallback, useEffect, useRef } from 'react';
import { isAnonymousUser } from '../utils/userUtils';

/**
 * Hook para gestionar sessionIds de conversaciones
 * Para anónimos: se limpia en refresh/close
 * Para autenticados: persiste en localStorage
 */
export const useSessionManager = () => {
    const STORAGE_KEY = 'conversationSessions';
    const SESSION_PREFIX = 'anon-session-';


    const isAnonymous = isAnonymousUser();


    const getStorage = useCallback(() => {
        return isAnonymous ? sessionStorage : localStorage;
    }, [isAnonymous]);

    const getStoredSessions = () => {
        try {
            const storage = getStorage();
            const stored = storage.getItem(STORAGE_KEY);

            if (stored) {
                const parsed = JSON.parse(stored);
                return parsed;
            }
            return {};
        } catch (error) {
            console.error('Error loading sessions:', error);
            return {};
        }
    };

    const [sessions, setSessions] = useState(getStoredSessions);
    const sessionsRef = useRef(sessions);

    useEffect(() => {
        sessionsRef.current = sessions;
    }, [sessions]);

    useEffect(() => {
        try {
            const storage = getStorage();
            storage.setItem(STORAGE_KEY, JSON.stringify(sessions));

            const storageType = isAnonymous ? 'sessionStorage' : 'localStorage';
        } catch (error) {
            console.error('Error saving sessions:', error);
        }
    }, [sessions, getStorage, isAnonymous]);


    useEffect(() => {
        if (!isAnonymous) {
            return; // No hacer nada para usuarios autenticados
        }

        // Solo para anónimos
        const handlePageUnload = (e) => {

            // Limpiar sessionStorage
            sessionStorage.clear();

            // Limpiar userUuid y email anónimos
            const userUuid = localStorage.getItem('currentUserUuid');
            if (userUuid && (userUuid.startsWith('user-') || userUuid.startsWith('anon-'))) {
                localStorage.removeItem('currentUserUuid');
                localStorage.removeItem('currentUserEmail');
                console.log('🗑️ Anonymous identifiers removed');
            }

            // Limpiar cookies de sesión anónima (si existen)
            document.cookie.split(";").forEach(cookie => {
                const name = cookie.split("=")[0].trim();
                if (name.includes('anon') || name.includes('session')) {
                    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
                }
            });
        };

        // beforeunload: cuando el usuario intenta salir (refresh, close, navigate away)
        window.addEventListener('beforeunload', handlePageUnload);

        // pagehide: más confiable en móviles
        window.addEventListener('pagehide', handlePageUnload);

        return () => {
            window.removeEventListener('beforeunload', handlePageUnload);
            window.removeEventListener('pagehide', handlePageUnload);
        };
    }, [isAnonymous]);


    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                if (isAnonymous) {

                } else {

                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [isAnonymous]);

    const getSessionId = useCallback((conversationId) => {
        if (!conversationId) {
            console.warn('getSessionId called without conversationId');
            return null;
        }

        const currentSessions = sessionsRef.current;

        if (currentSessions[conversationId]) {
            return currentSessions[conversationId];
        }

        const newSessionId = `${SESSION_PREFIX}${crypto.randomUUID()}`;

        setSessions(prev => ({
            ...prev,
            [conversationId]: newSessionId
        }));

        return newSessionId;
    }, [isAnonymous]);

    const updateSessionId = useCallback((conversationId, sessionId) => {
        if (!conversationId || !sessionId) {
            console.warn('updateSessionId called with invalid params:', { conversationId, sessionId });
            return;
        }

        setSessions(prev => {
            if (prev[conversationId] === sessionId) {
                return prev;
            }

            return {
                ...prev,
                [conversationId]: sessionId
            };
        });
    }, []);

    const clearSession = useCallback((conversationId) => {
        if (!conversationId) {
            console.warn('clearSession called without conversationId');
            return;
        }

        setSessions(prev => {
            if (!prev[conversationId]) {
                return prev;
            }

            const { [conversationId]: removed, ...rest } = prev;
            return rest;
        });
    }, []);

    const clearAllSessions = useCallback(() => {
        setSessions({});

        const storage = getStorage();
        storage.removeItem(STORAGE_KEY);

        // Si es anónimo, limpiar también identifiers
        if (isAnonymous) {
            sessionStorage.clear();
            const userUuid = localStorage.getItem('currentUserUuid');
            if (userUuid && (userUuid.startsWith('user-') || userUuid.startsWith('anon-'))) {
                localStorage.removeItem('currentUserUuid');
                localStorage.removeItem('currentUserEmail');
            }
        }

    }, [getStorage, isAnonymous]);

    const forceNewSession = useCallback((conversationId) => {
        if (!conversationId) return null;

        const newSessionId = `${SESSION_PREFIX}${crypto.randomUUID()}`;

        setSessions(prev => ({
            ...prev,
            [conversationId]: newSessionId
        }));

        return newSessionId;
    }, []);

    const getSessionStats = useCallback(() => {
        return {
            total: Object.keys(sessionsRef.current).length,
            sessions: { ...sessionsRef.current },
            isAnonymous,
            storage: isAnonymous ? 'sessionStorage (temp)' : 'localStorage (persistent)'
        };
    }, [isAnonymous]);

    return {
        getSessionId,
        updateSessionId,
        clearSession,
        clearAllSessions,
        forceNewSession,
        getSessionStats,
        isAnonymous
    };
};